import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { addSubformFields, addSubform, addSubformFieldsBulk, addSubformBulk } from 'apps/web_forms/slices/formSlice';
import { getIdWithoutSuffix } from 'apps/web_forms/utils/inputsUtils';

import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import FormInputs from 'apps/web_forms/components/Form/FormInputs';
import { COLORS, DEVICES_SIZES } from 'apps/web_forms/utils/constants';
import useRules from 'apps/web_forms/hooks/useRules';
import useGroupProgress from 'apps/web_forms/hooks/useGroupProgress';
import ProgressCircle from 'apps/web_forms/components/commons/ProgressCircle';
import ComponentModal from 'apps/web_forms/components/commons/ComponentModal';
import MediaFormControls from 'apps/web_forms/components/MediaFormControls';
import FormIcon from 'apps/web_forms/assets/form.svg';
import useGlobalSelector from 'apps/web_forms/hooks/useGlobalSelector';

const StyledFromInputs = styled.div`
  &.inactive {
    display: none;
  }
  background-color: ${COLORS.inputBackgroundColor};
  border: 1px solid ${COLORS.grey};
  padding: 1em;
  & > .sub-form-title-container {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    cursor: initial;
    &.clickable-title {
      cursor: pointer;
    }
    .completed-circle {
      position: relative;
      margin-right: 1em;
    }
    h4 {
      font-weight: 300;
      font-size: 0.95em;
      margin-bottom: none;
      flex-grow: 1;
    }
    .subform-contains-inputs{
      svg {
        fill: #b5b5b5;
      }
    }
    .expand-btn {
      transform: rotate(180deg);
      display: block;
      border: none;
      background: transparent;
      pointer: none;
      svg {
        width: 1.3em;
        height: 1.3em;
      }
    } 
  }
  .subform-contains-inputs {
    polygon {
      fill: ${COLORS.lightgrey};
    }
  }
  .sub-forms-inputs-container {
    width: 100%;
    .elements-container {
      width: 100%;
    }
  }
  .completed-circle .check-icon {
    position: absolute;
    width: 16px;
    height: 16px;
    top: calc(50% - 8px);
    left: calc(50% - 8px);
    border-radius: 50%;
    transition: all 200ms;
  }
`;

const StyledFormElementsContainer = styled.div`
  background-color: white;
  padding: 1.9em;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  flex-grow: 1;
  overflow: auto;
  .elements-container {
    width: 100%;
    padding-right: 1em;
  }
  @media (min-width: ${DEVICES_SIZES.tablet}) {
    .elements-container {
      width: ${(props) => (props.isOpen ? '59%' : '95%')};
      padding: 0;
      transition: 0.5s;
    }
  } ;
`;

const SubForm = ({ element, formContext, dispatchInitialAnswers }) => {
  const { title, hint, identifier } = element;
  const [{ isActive }] = useRules(element, formContext);
  const [mutatedElement, setMutatedElement] = useState(element)
  const [openModal, setOpenModal] = useState(false);
  const {subformFields} = useGlobalSelector();
  const [width, setWidth] = useState(window.innerWidth);
  const containsInputs = element.children.some(item => item.type !== 'group');
  
  const dispatch = useDispatch();
  const { completedPercentage, updateField } = useGroupProgress(identifier);

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
        window.removeEventListener('resize', handleWindowSizeChange);
    }
  }, []);

  useEffect(() => {
    if (dispatchInitialAnswers) {
      // we are on a draft
      if (window.location.href.includes('origin=draft')) {
        return;
      }

      dispatchInitialAnswers.forEach((initialAnswerList) => {
        if (Object.keys(initialAnswerList).length) {
          const boundChildren = element.children.map((childElement) => {
            const [fieldIdentifier] = childElement.identifier
              .split('/')
              .slice(-1);

            const currentInitialAnswer = initialAnswerList[fieldIdentifier];

            if (currentInitialAnswer) {
              const boundChild = {
                ...childElement,
                hasInitialAnswer: true,
              };

              if (currentInitialAnswer instanceof Array) {
                boundChild.dispatchInitialAnswers = currentInitialAnswer;
              } else {
                boundChild.initialAnswer = currentInitialAnswer;
              }

              return boundChild;
            }

            return childElement;
          });
          setMutatedElement({...element, children: boundChildren })
        }
      });
    }
  }, [dispatchInitialAnswers]);

  useEffect( () => {
    function storeSubformFields(fields, updateSubformSlice = [], updateSubformFieldsSlice = []) {

      fields.forEach((field) => {
        if (field.children && field.maxOccurs) {
          // Subform
          updateSubformSlice.push(field.identifier);
          storeSubformFields(field.children, updateSubformSlice, updateSubformFieldsSlice);
        } else if (!field.children) {
          const identifierWithoutSuffixes = getIdWithoutSuffix(field.identifier);
  
          /**
           * Checks if the field exists in subformField redux state or not. It should add it only if it doesn't exists.
           */
          if (
            !subformFields.hasOwnProperty(identifierWithoutSuffixes) ||
            subformFields[identifierWithoutSuffixes].indexOf(field.identifier) === -1
          ) {
            updateSubformFieldsSlice.push(field.identifier);
          }
        }
      });

      if (fieldTracker.length) {
        dispatch(addSubformBulk(subformTracker))
       dispatch(addSubformFieldsBulk(fieldTracker))
      }
    }
  
  }, []);

  function onChildChangeValue(childIdentifier, value) {
    updateField(childIdentifier, value);
  }

  const openSubformContainer = (e) => {
    e.stopPropagation();
    if(containsInputs){
      setOpenModal(true);
    }
  }

  return (
    <div
      className={`
        react-form-control
        ${!isActive ? 'inactive' : ''}
      `}
    >
     {openModal && <ComponentModal
        mainMessage={title}
        isOpen={openModal}
        setIsOpen={setOpenModal}
        isFullScreen={width <= 768}
        children={
          <StyledFormElementsContainer>
            <FormInputs 
              elements={mutatedElement.children} 
              setValue={formContext.setValue} 
              onChildChangeValue={onChildChangeValue} 
              isModal={true} 
              isInsideGroupModal={true}
              showContent={true}
            />
            <MediaFormControls />
          </StyledFormElementsContainer>
        }
      />}
      <StyledFromInputs>
        <div className={containsInputs ? 'sub-form-title-container clickable-title' : 'sub-form-title-container'} onClick={openSubformContainer}>
          <div className='completed-circle'>
            <ProgressCircle progress={completedPercentage} />
          </div>
          <h4>{title}</h4>
          {containsInputs && <div className='subform-contains-inputs'><FormIcon /></div>}
        </div>
        <div className='sub-forms-inputs-container group-container'>
          <FormInputs
            elements={mutatedElement.children}
            setValue={formContext.setValue} 
            onChildChangeValue={onChildChangeValue}
            isInsideGroupModal={true}
            showContent={false}
          />
        </div>
      </StyledFromInputs>
      {hint && <p className='element-hint'>{hint}</p>}
    </div>
  );
};

function lazyFunction(f) {
  return function () {
    return f.apply(this);
  };
}

const lazyElement = lazyFunction(function () {
  return elementType;
});

const elementType = PropTypes.shape({
  identifier: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(lazyElement),
  hint: PropTypes.string,
});

SubForm.propTypes = {
  element: elementType.isRequired,
  formContext: customPropTypes.formContext.isRequired,
};

export default React.memo(SubForm);